import { all, put, call, select, getContext, takeEvery, spawn } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import _merge from 'lodash/merge';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';

import { buildUrl } from './utils';
import { getLogger } from '../logger/index';
import { getHashcode, template } from '../utils/misc';

const logger = getLogger('autocomplete-descriptor');

export function createAutocompleteSaga(url, descriptor, defaultOptions, logger) {
  const dataTimeToLive = descriptor.timeToLive || 60000;
  const urlTemplate = template(url);
  const defaultFilters = defaultOptions.defaultFilters || {};

  const getLocale = createSelector(
    state => state.intl,
    state => state.locale
  );

  const { getId, getLabel } = descriptor;
  let lastOptionsHashcode = null;

  return function* ensureDataSaga({ payload: { options = {} } }) {
    try {
      let data = yield select(descriptor.selectors.getData);
      const isLoading = yield select(descriptor.selectors.isLoading);
      const lastUpdate = yield select(descriptor.selectors.lastUpdate);
      const optionsHashcode = getHashcode(options);
      logger.trace(
        'Check data',
        descriptor.name,
        data,
        '- isLoading',
        isLoading,
        '- livesFor',
        Date.now() - lastUpdate,
        '- TimeToLive',
        dataTimeToLive,
        '- OptionsHashcode',
        optionsHashcode,
        '- LastOptionsHashcode',
        lastOptionsHashcode
      );
      if (Date.now() - lastUpdate > dataTimeToLive || data == null || optionsHashcode !== lastOptionsHashcode) {
        lastOptionsHashcode = optionsHashcode;
        const http = yield getContext('http');
        const locale = yield select(getLocale);
        const urlParameter = { options };
        const requestUrl = buildUrl(
          urlTemplate(urlParameter),
          _merge(defaultFilters, options && options.filters),
          locale
        );
        const requestConfig = defaultOptions.requestConfig
          ? { ...defaultOptions.requestConfig, ...options.requestConfig }
          : options.requestConfig;

        logger.debug('Fetch autocomplete data URL', url, 'defaultOptions', defaultOptions, 'Options', options);
        logger.debug('Fetch autocomplete data RequestUrl', requestUrl, 'RequestConfig', requestConfig);

        const response = yield call(http.get, requestUrl);
        logger.debug('Fetch autocomplete data Response', response);
        if (response.status < 200 || response.status > 399) {
          logger.error('HTTP error while ensuring data for', descriptor.name, response);
          throw response.data;
        }
        data = response.data.list.map(entry => ({
          id: getId(entry, locale),
          label: getLabel(entry, locale),
          data: entry,
        }));
        yield put(descriptor.actions.setData(data, Date.now()));
      } else {
        logger.trace('Data already available for', descriptor);
        yield put(descriptor.actions.setData(data));
      }
    } catch (e) {
      logger.error('Error while ensuring data for', descriptor.name, e);
      yield put(descriptor.actions.dataError(e));
    }
  };
}

function createWatcher(actionsSagaMap) {
  return function* watchAutocompleteActions() {
    yield all(
      actionsSagaMap.map(entry =>
        spawn(function* watchAutocompleteAction() {
          yield takeEvery(entry.action, entry.handler);
        })
      )
    );
  };
}

export function createAutocompleteActionTypes(actionPrefix) {
  return {
    ENSURE_DATA: actionPrefix + '_ENSURE_DATA',
    SET_DATA: actionPrefix + '_SET_DATA',
    DATA_ERROR: actionPrefix + '_DATA_ERROR',
    DATA_REMOVE_ERROR: actionPrefix + '_DATA_REMOVE_ERROR',
    DATA_CLEAR_ERRORS: actionPrefix + '_DATA_CLEAR_ERRORS',
  };
}

export function createAutocompleteActions(actionTypes) {
  return {
    ensureData: options => ({ type: actionTypes.ENSURE_DATA, payload: { options } }),
    setData: (data, lastUpdate) => ({ type: actionTypes.SET_DATA, payload: { data, lastUpdate } }),
    dataError: error => ({ type: actionTypes.DATA_ERROR, payload: { error }, error: true }),
    removeError: index => ({ type: actionTypes.DATA_REMOVE_ERROR, payload: { index } }),
    clearErrors: () => ({ type: actionTypes.DATA_CLEAR_ERRORS }),
  };
}

export function createAutocompleteReduceFunctions(actionTypes, moduleName, name, reducerContainer) {
  return {
    ...reducerContainer,
    [actionTypes.ENSURE_DATA]: state => ({
      ...state,
      autocomplete: {
        ...(state.autocomplete || {}),
        [name]: { ..._get(state, ['autocomplete', name], {}), pending: true },
      },
    }),
    [actionTypes.SET_DATA]: (state, { payload: { data, lastUpdate } }) => ({
      ...state,
      autocomplete: {
        ...state.autocomplete,
        [name]: {
          ...state.autocomplete[name],
          pending: false,
          data,
          lastUpdate: lastUpdate || state.autocomplete[name].lastUpdate,
        },
      },
    }),
    [actionTypes.DATA_ERROR]: (state, { payload: { error } }) => ({
      ...state,
      autocomplete: {
        ...state.autocomplete,
        [name]: {
          ...state.autocomplete[name],
          pending: false,
          errors: [...state.autocomplete[name].errors, error],
        },
      },
    }),
    [actionTypes.DATA_REMOVE_ERROR]: (state, { payload: { index } }) => ({
      ...state,
      autocomplete: {
        ...state.autocomplete,
        [name]: {
          ...state.autocomplete[name],
          pending: false,
          errors: state.autocomplete[name].errors.filter(_, errorIndex => index === errorIndex),
        },
      },
    }),
    [actionTypes.DATA_CLEAR_ERRORS]: state => ({
      ...state,

      autocomplete: {
        ...state.autocomplete,
        [name]: {
          ...state.autocomplete[name],
          pending: false,
          errors: [],
        },
      },
    }),
  };
}

function createAutocompleteSelectors(moduleName, name) {
  const baseSelector = state =>
    (state[moduleName] && state[moduleName].autocomplete && state[moduleName].autocomplete[name]) || {};
  return {
    getData: createSelector(baseSelector, state => state.data),
    isLoading: createSelector(baseSelector, state => state.pending),
    lastUpdate: createSelector(baseSelector, state => (state.lastUpdate != null ? state.lastUpdate : Number.MIN_VALUE)),
  };
}

export function createAutocomplete(moduleName, definitions) {
  const result = { __reducer: {} };
  let reducerContainer = {};
  definitions.forEach(definition => {
    const { name } = definition;
    const actionPrefix = name.toUpperCase() + '_AUTOCOMPLETE';
    const actionTypes = createAutocompleteActionTypes(actionPrefix);
    const actions = createAutocompleteActions(actionTypes);
    const selectors = createAutocompleteSelectors(moduleName, name);
    reducerContainer = createAutocompleteReduceFunctions(actionTypes, moduleName, name, reducerContainer);
    const __saga = createWatcher([
      {
        action: actionTypes.ENSURE_DATA,
        handler: createAutocompleteSaga(
          definition.url,
          { ...definition, actions, selectors },
          definition.options || {},
          logger
        ),
      },
    ]);
    result[name] = {
      actionTypes,
      actions,
      selectors,
      __saga,
    };
  });
  let autocompleteInitialized = false;
  result.__reducer = (state = {}, action) => {
    if (!autocompleteInitialized) {
      autocompleteInitialized = true;
      let { autocomplete = {} } = state || {};
      definitions.forEach(definition => {
        autocomplete = {
          ...autocomplete,
          [definition.name]: {
            pending: false,
            lastUpdate: Date.now() - (definition.timeToLive || 60000) * 2,
            data: [],
            errors: [],
          },
        };
      });
      state.autocomplete = autocomplete;
    }
    if (reducerContainer[action.type]) {
      return reducerContainer[action.type](state, action);
    }
    return state;
  };
  return result;
}
